import { useState, useEffect } from "react";
import { Plus, Edit, Trash2, X } from "lucide-react";
import AdminLayout from "@/components/AdminLayout";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { adminApi, publicApi } from "@/lib/api";
import { toast } from "sonner";

const CATEGORIES = ["Honeymoon", "Adventure", "Family", "Luxury", "Budget", "Group"];

const initialFormState = {
  name: "",
  category: "",
  duration: "",
  price: "",
  description: "",
  image_url: "",
  is_featured: false,
  highlights: [""],
  inclusions: [""],
  exclusions: [""],
  itinerary: [{ day: "1", title: "", description: "" }],
  pricing_tiers: [],
};

const AdminPackages = () => {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  const [formData, setFormData] = useState(initialFormState);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchPackages();
  }, []);

  const fetchPackages = async () => {
    try {
      const response = await publicApi.get("/packages");
      setPackages(response.data);
    } catch (error) {
      console.error("Error fetching packages:", error);
      toast.error("Failed to load packages");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this package?")) return;

    try {
      await adminApi.delete(`/admin/packages/${id}`);
      toast.success("Package deleted successfully");
      fetchPackages();
    } catch (error) {
      toast.error("Failed to delete package");
    }
  };

  const openAddModal = () => {
    setEditingPackage(null);
    setFormData(initialFormState);
    setShowModal(true);
  };

  const openEditModal = (pkg) => {
    setEditingPackage(pkg);
    setFormData({
      name: pkg.name,
      category: pkg.category,
      duration: pkg.duration,
      price: pkg.price.toString(),
      description: pkg.description,
      image_url: pkg.image_url,
      is_featured: pkg.is_featured,
      highlights: pkg.highlights.length > 0 ? pkg.highlights : [""],
      inclusions: pkg.inclusions.length > 0 ? pkg.inclusions : [""],
      exclusions: pkg.exclusions.length > 0 ? pkg.exclusions : [""],
      itinerary: pkg.itinerary.length > 0 ? pkg.itinerary : [{ day: "1", title: "", description: "" }],
      pricing_tiers: Array.isArray(pkg.pricing_tiers) ? pkg.pricing_tiers.map(t => ({
        min_pax: String(t.min_pax ?? ""),
        max_pax: t.max_pax == null ? "" : String(t.max_pax),
        price_per_person: String(t.price_per_person ?? "")
      })) : [],
    });
    setShowModal(true);
  };

  // Array field handlers
  const handleArrayFieldChange = (field, index, value) => {
    const updated = [...formData[field]];
    updated[index] = value;
    setFormData({ ...formData, [field]: updated });
  };

  const addArrayField = (field) => {
    setFormData({ ...formData, [field]: [...formData[field], ""] });
  };

  const removeArrayField = (field, index) => {
    if (formData[field].length === 1) return;
    const updated = formData[field].filter((_, i) => i !== index);
    setFormData({ ...formData, [field]: updated });
  };

  // Itinerary handlers
  const handleItineraryChange = (index, key, value) => {
    const updated = [...formData.itinerary];
    updated[index] = { ...updated[index], [key]: value };
    setFormData({ ...formData, itinerary: updated });
  };

  const addItineraryDay = () => {
    const nextDay = (formData.itinerary.length + 1).toString();
    setFormData({
      ...formData,
      itinerary: [...formData.itinerary, { day: nextDay, title: "", description: "" }],
    });
  };

  const removeItineraryDay = (index) => {
    if (formData.itinerary.length === 1) return;
    const updated = formData.itinerary.filter((_, i) => i !== index);
    setFormData({ ...formData, itinerary: updated });
  };

  // Pricing tiers handlers (for Group packages)
  const handleTierChange = (index, key, value) => {
    const updated = [...formData.pricing_tiers];
    updated[index] = { ...updated[index], [key]: value };
    setFormData({ ...formData, pricing_tiers: updated });
  };
  const addTier = () => {
    setFormData({
      ...formData,
      pricing_tiers: [...formData.pricing_tiers, { min_pax: "", max_pax: "", price_per_person: "" }],
    });
  };
  const removeTier = (index) => {
    setFormData({
      ...formData,
      pricing_tiers: formData.pricing_tiers.filter((_, i) => i !== index),
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    const packageData = {
      name: formData.name,
      category: formData.category,
      duration: formData.duration,
      price: parseFloat(formData.price),
      description: formData.description,
      image_url: formData.image_url,
      is_featured: formData.is_featured,
      highlights: formData.highlights.filter((h) => h.trim()),
      inclusions: formData.inclusions.filter((i) => i.trim()),
      exclusions: formData.exclusions.filter((e) => e.trim()),
      itinerary: formData.itinerary.filter((d) => d.title.trim() && d.description.trim()),
      pricing_tiers: (formData.pricing_tiers || [])
        .filter((t) => t.min_pax && t.price_per_person)
        .map((t) => ({
          min_pax: parseInt(t.min_pax, 10),
          max_pax: t.max_pax === "" || t.max_pax == null ? null : parseInt(t.max_pax, 10),
          price_per_person: parseFloat(t.price_per_person),
        })),
    };

    try {
      if (editingPackage) {
        await adminApi.put(`/admin/packages/${editingPackage.id}`, packageData);
        toast.success("Package updated successfully");
      } else {
        await adminApi.post("/admin/packages", packageData);
        toast.success("Package created successfully");
      }
      setShowModal(false);
      fetchPackages();
    } catch (error) {
      console.error("Error saving package:", error);
      toast.error(error.response?.data?.detail || "Failed to save package");
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminLayout>
      <div data-testid="admin-packages">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-serif">Manage Packages</h1>
            <p className="text-muted-foreground mt-1">Total: {packages.length} packages</p>
          </div>
          <Button onClick={openAddModal} data-testid="add-package-btn" className="bg-primary hover:bg-primary/90">
            <Plus className="mr-2" size={18} />
            Add New Package
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="spinner"></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {packages.map((pkg) => (
              <div key={pkg.id} className="bg-white rounded-lg overflow-hidden shadow-sm border border-border/50" data-testid={`package-${pkg.id}`}>
                <img src={pkg.image_url} alt={pkg.name} className="w-full h-48 object-cover" />
                <div className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs bg-secondary/20 text-primary px-2 py-1 rounded font-medium">
                      {pkg.category}
                    </span>
                    {pkg.is_featured && (
                      <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded font-medium">Featured</span>
                    )}
                  </div>
                  <h3 className="text-lg font-serif mb-2">{pkg.name}</h3>
                  <p className="text-sm text-muted-foreground mb-2">{pkg.duration}</p>
                  <p className="text-xl font-bold text-primary mb-4">₹{pkg.price.toLocaleString('en-IN')}</p>
                  <div className="flex gap-2">
                    <Button onClick={() => openEditModal(pkg)} size="sm" variant="outline" className="flex-1" data-testid={`edit-${pkg.id}`}>
                      <Edit size={14} className="mr-1" />
                      Edit
                    </Button>
                    <Button onClick={() => handleDelete(pkg.id)} size="sm" variant="destructive" data-testid={`delete-${pkg.id}`}>
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Add/Edit Modal */}
        <Dialog open={showModal} onOpenChange={setShowModal}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="package-form-modal">
            <DialogHeader>
              <DialogTitle className="text-2xl font-serif">
                {editingPackage ? "Edit Package" : "Add New Package"}
              </DialogTitle>
              <DialogDescription>
                Fill in the details below to {editingPackage ? "update" : "create"} a tour package.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-6 mt-4">
              {/* Basic Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Package Name *</label>
                  <Input
                    data-testid="form-name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    placeholder="Romantic Honeymoon Escape"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Category *</label>
                  <Select
                    value={formData.category}
                    onValueChange={(value) => setFormData({ ...formData, category: value })}
                  >
                    <SelectTrigger data-testid="form-category">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map((cat) => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Duration *</label>
                  <Input
                    data-testid="form-duration"
                    value={formData.duration}
                    onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                    required
                    placeholder="5 Days / 4 Nights"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Price (INR) *</label>
                  <Input
                    data-testid="form-price"
                    type="number"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    required
                    placeholder="45999.00"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Image URL *</label>
                <Input
                  data-testid="form-image-url"
                  value={formData.image_url}
                  onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                  required
                  placeholder="https://images.unsplash.com/..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Description *</label>
                <Textarea
                  data-testid="form-description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  required
                  rows={4}
                  placeholder="Describe the package..."
                />
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="is_featured"
                  data-testid="form-featured"
                  checked={formData.is_featured}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_featured: checked })}
                />
                <label htmlFor="is_featured" className="text-sm font-medium cursor-pointer">
                  Mark as Featured Package (shown on homepage)
                </label>
              </div>

              {/* Highlights */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium">Highlights</label>
                  <Button type="button" size="sm" variant="outline" onClick={() => addArrayField("highlights")}>
                    <Plus size={14} className="mr-1" /> Add
                  </Button>
                </div>
                {formData.highlights.map((item, index) => (
                  <div key={index} className="flex gap-2 mb-2">
                    <Input
                      value={item}
                      onChange={(e) => handleArrayFieldChange("highlights", index, e.target.value)}
                      placeholder="e.g., Private beach access"
                    />
                    <Button type="button" size="sm" variant="ghost" onClick={() => removeArrayField("highlights", index)}>
                      <X size={16} />
                    </Button>
                  </div>
                ))}
              </div>

              {/* Inclusions */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium">Inclusions</label>
                  <Button type="button" size="sm" variant="outline" onClick={() => addArrayField("inclusions")}>
                    <Plus size={14} className="mr-1" /> Add
                  </Button>
                </div>
                {formData.inclusions.map((item, index) => (
                  <div key={index} className="flex gap-2 mb-2">
                    <Input
                      value={item}
                      onChange={(e) => handleArrayFieldChange("inclusions", index, e.target.value)}
                      placeholder="e.g., 4 nights hotel accommodation"
                    />
                    <Button type="button" size="sm" variant="ghost" onClick={() => removeArrayField("inclusions", index)}>
                      <X size={16} />
                    </Button>
                  </div>
                ))}
              </div>

              {/* Exclusions */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium">Exclusions</label>
                  <Button type="button" size="sm" variant="outline" onClick={() => addArrayField("exclusions")}>
                    <Plus size={14} className="mr-1" /> Add
                  </Button>
                </div>
                {formData.exclusions.map((item, index) => (
                  <div key={index} className="flex gap-2 mb-2">
                    <Input
                      value={item}
                      onChange={(e) => handleArrayFieldChange("exclusions", index, e.target.value)}
                      placeholder="e.g., Airfare"
                    />
                    <Button type="button" size="sm" variant="ghost" onClick={() => removeArrayField("exclusions", index)}>
                      <X size={16} />
                    </Button>
                  </div>
                ))}
              </div>

              {/* Itinerary */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium">Itinerary (Day-by-Day)</label>
                  <Button type="button" size="sm" variant="outline" onClick={addItineraryDay}>
                    <Plus size={14} className="mr-1" /> Add Day
                  </Button>
                </div>
                {formData.itinerary.map((day, index) => (
                  <div key={index} className="border border-border/50 rounded-lg p-4 mb-3 bg-muted/30">
                    <div className="flex items-center justify-between mb-3">
                      <span className="font-semibold text-primary">Day {day.day}</span>
                      <Button type="button" size="sm" variant="ghost" onClick={() => removeItineraryDay(index)}>
                        <X size={16} />
                      </Button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <Input
                        value={day.day}
                        onChange={(e) => handleItineraryChange(index, "day", e.target.value)}
                        placeholder="Day number"
                      />
                      <Input
                        value={day.title}
                        onChange={(e) => handleItineraryChange(index, "title", e.target.value)}
                        placeholder="Day title"
                        className="md:col-span-2"
                      />
                    </div>
                    <Textarea
                      value={day.description}
                      onChange={(e) => handleItineraryChange(index, "description", e.target.value)}
                      placeholder="Day description..."
                      rows={2}
                      className="mt-3"
                    />
                  </div>
                ))}
              </div>

              {/* Group Pricing Tiers */}
              <div>
                <div className="flex justify-between items-center mb-3">
                  <div>
                    <label className="block text-sm font-medium">Group Pricing Tiers (optional)</label>
                    <p className="text-xs text-muted-foreground mt-1">Per-person rates by group size. Leave "Max Pax" empty for the top tier (e.g. 30+).</p>
                  </div>
                  <Button type="button" size="sm" variant="outline" onClick={addTier} data-testid="add-tier-btn">
                    <Plus size={14} className="mr-1" /> Add Tier
                  </Button>
                </div>
                {formData.pricing_tiers.length === 0 ? (
                  <p className="text-xs text-muted-foreground italic bg-muted/50 rounded p-3">
                    No tiered pricing. The "Price" field above will be used as the total booking amount.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {formData.pricing_tiers.map((tier, index) => (
                      <div key={index} data-testid={`tier-row-${index}`} className="grid grid-cols-[1fr_1fr_1.4fr_auto] gap-2 items-center bg-muted/30 rounded p-2">
                        <Input type="number" min="1" placeholder="Min pax" value={tier.min_pax}
                          onChange={(e) => handleTierChange(index, "min_pax", e.target.value)}
                          data-testid={`tier-min-${index}`} />
                        <Input type="number" min="1" placeholder="Max pax (blank = ∞)" value={tier.max_pax}
                          onChange={(e) => handleTierChange(index, "max_pax", e.target.value)}
                          data-testid={`tier-max-${index}`} />
                        <Input type="number" min="0" step="1" placeholder="₹ per person" value={tier.price_per_person}
                          onChange={(e) => handleTierChange(index, "price_per_person", e.target.value)}
                          data-testid={`tier-price-${index}`} />
                        <button type="button" onClick={() => removeTier(index)}
                          data-testid={`tier-remove-${index}`}
                          className="text-red-600 hover:bg-red-50 p-2 rounded" aria-label="Remove tier">
                          <X size={14} />
                        </button>
                      </div>
                    ))}
                    <p className="text-xs text-muted-foreground pl-1">
                      Booking total = per-person rate × total pax (adults + children).
                    </p>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setShowModal(false)} className="flex-1">
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={saving}
                  data-testid="save-package-btn"
                  className="flex-1 bg-primary hover:bg-primary/90"
                >
                  {saving ? "Saving..." : editingPackage ? "Update Package" : "Create Package"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminPackages;
